import {
  getAllItemsInCategory,
  getBudgetFringeTotals,
  getBudgetTotalActual,
  getBudgetTotalEstimate,
  getBudgetTotalVariance,
  getFringesFromBudgetItems,
} from "./BudgetItemUtils";
import {
  BudgetCategory,
  BudgetContingency,
  BudgetI,
  BudgetSubCategory,
  BudgetTotal,
} from "./types";

export type BudgetTopSheetViewMode = "outline" | "classic";

export interface BudgetCategoryFringes {
  name: string;
  category_id: number;
  account_number?: string;
  estimate: number;
  actual_amount: number;
  variance: number;
  nested_fringes: { name: string }[];
}

export type TopSheetBodyRow = BudgetCategory | BudgetSubCategory | BudgetCategoryFringes;

export interface TopSheetRowsResult {
  bodyRows: TopSheetBodyRow[] | null;
  pinnedRows: (BudgetTotal | BudgetContingency)[];
}

/** Stable key for category / sub-category order — used to detect structure reorder in the grid. */
export function getTopSheetStructureOrderKey(sheetData: BudgetCategory[]): string {
  return sheetData
    .flatMap((category) => [
      category.budget_category_id,
      ...category.sub_categories.map((subCategory) => subCategory.budget_sub_category_id),
    ])
    .join(",");
}

/** All categories render on the top sheet (no artificial cap). */
export const getTopSheetCategories = (sheetData: BudgetCategory[]): BudgetCategory[] => sheetData;

/** @deprecated Use getTopSheetCategories — kept for tests migrating off the old six-category cap. */
export const TOP_SHEET_CATEGORY_LIMIT = Number.POSITIVE_INFINITY;

/** Category ids shown on the top sheet (all categories). */
export const getTopSheetCategoryIds = (sheetData: BudgetCategory[]): number[] =>
  getTopSheetCategories(sheetData).map((category) => category.budget_category_id);

export const instanceOfBudgetCategoryFringes = (object: unknown): object is BudgetCategoryFringes =>
  typeof object === "object" &&
  object !== null &&
  "nested_fringes" in object &&
  "category_id" in object;

export function getTopSheetCategoryAccountNumber(category: BudgetCategory): string {
  const firstSubCategory = category.sub_categories[0];
  if (firstSubCategory?.account_number) {
    return firstSubCategory.account_number;
  }
  return category.fringe_account_number ?? "";
}

const getCategoryFringes = (
  category: BudgetCategory,
  allCategories: BudgetCategory[]
): BudgetCategoryFringes => {
  const categoryFringes = getFringesFromBudgetItems(getAllItemsInCategory(category));
  const categoryIndex = allCategories.findIndex(
    (candidate) => candidate.budget_category_id === category.budget_category_id
  );
  const allocation = getBudgetFringeTotals(allCategories).categories[categoryIndex];

  return {
    name: `${category.name} FRINGES`,
    account_number: category.fringe_account_number,
    category_id: category.budget_category_id,
    estimate: allocation.estimate,
    actual_amount: allocation.actual,
    variance: allocation.actual ? allocation.estimate - allocation.actual : 0,
    nested_fringes: categoryFringes.nested_fringes,
  };
};

function getExpandedCategoryRows(
  category: BudgetCategory,
  normalizedQuickFilter: string,
  allCategories: BudgetCategory[]
): TopSheetBodyRow[] {
  const rows: TopSheetBodyRow[] = [];

  category.sub_categories.forEach((subCategory) => {
    if (
      !normalizedQuickFilter ||
      subCategory.name.toLowerCase().includes(normalizedQuickFilter) ||
      subCategory.account_number.toLowerCase().includes(normalizedQuickFilter)
    ) {
      rows.push({ ...subCategory });
    }
  });

  const categoryFringes = getCategoryFringes(category, allCategories);
  if (categoryFringes.nested_fringes.length > 0) {
    rows.push(categoryFringes);
  }

  return rows;
}

export function buildTopSheetBodyRows({
  sheetData,
  collapsedCategoryIds,
  quickFilter,
  viewMode = "outline",
}: {
  sheetData: BudgetCategory[];
  collapsedCategoryIds: number[];
  quickFilter: string;
  viewMode?: BudgetTopSheetViewMode;
}): TopSheetBodyRow[] {
  const normalizedQuickFilter = quickFilter.trim().toLowerCase();
  const bodyRows: TopSheetBodyRow[] = [];

  getTopSheetCategories(sheetData).forEach((category) => {
    const isCollapsed = collapsedCategoryIds.includes(category.budget_category_id);
    const expandedRows = isCollapsed
      ? []
      : getExpandedCategoryRows(category, normalizedQuickFilter, sheetData);

    if (viewMode === "classic" && !isCollapsed) {
      bodyRows.push(...expandedRows);
      bodyRows.push({ ...category });
      return;
    }

    bodyRows.push({ ...category });
    bodyRows.push(...expandedRows);
  });

  return bodyRows;
}

export function buildTopSheetPinnedRows(
  sheetData: BudgetCategory[],
  budgetDetail: Pick<
    BudgetI,
    "contingency_percentage" | "contingency_locked" | "contingency_actual"
  >,
  bodyRows: TopSheetBodyRow[]
): (BudgetTotal | BudgetContingency)[] {
  if (bodyRows.length === 0) {
    return [];
  }

  const contingency = {
    contingency_percentage: budgetDetail.contingency_percentage,
    locked: budgetDetail.contingency_locked,
    actual_amount: budgetDetail.contingency_actual,
  };

  return [
    {
      contingency_percentage: budgetDetail.contingency_percentage,
      locked: budgetDetail.contingency_locked,
      actual_amount: budgetDetail.contingency_actual,
    },
    {
      name: "Grand Total",
      estimate: getBudgetTotalEstimate(sheetData, contingency),
      actual_amount: getBudgetTotalActual(sheetData, contingency),
      variance: getBudgetTotalVariance(sheetData, {
        contingency_percentage: budgetDetail.contingency_percentage,
        locked: budgetDetail.contingency_locked,
        actual_amount: budgetDetail.contingency_actual,
      }),
    },
  ];
}

export function buildTopSheetRows({
  sheetData,
  budgetDetail,
  collapsedCategoryIds,
  quickFilter,
  hasGridApi,
  viewMode = "outline",
}: {
  sheetData: BudgetCategory[] | undefined;
  budgetDetail: Omit<BudgetI, "categories"> | undefined;
  collapsedCategoryIds: number[];
  quickFilter: string;
  hasGridApi: boolean;
  viewMode?: BudgetTopSheetViewMode;
}): TopSheetRowsResult {
  if (!sheetData || !budgetDetail) {
    return { bodyRows: null, pinnedRows: [] };
  }
  if (!hasGridApi) {
    return { bodyRows: [], pinnedRows: [] };
  }

  const bodyRows = buildTopSheetBodyRows({
    sheetData,
    collapsedCategoryIds,
    quickFilter,
    viewMode,
  });
  const pinnedRows = buildTopSheetPinnedRows(sheetData, budgetDetail, bodyRows);

  return { bodyRows, pinnedRows };
}
